import { useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck, Download, AlertCircle } from 'lucide-react';
import { getApiBase } from './apiBase';
import { downloadCsv } from './AiCopilot/downloadCsv';
import { loadFusionUsersDirectory } from './AiCopilot/securityAccessMapApi';

const API = axios.create({ baseURL: getApiBase(), withCredentials: true, timeout: 300000 });

export default function SodBulkScan() {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);
  const [results, setResults] = useState([]);
  const [limit, setLimit] = useState(25);

  async function loadDirectory() {
    setLoading(true);
    setError('');
    setResults([]);
    try {
      const data = await loadFusionUsersDirectory();
      setUsers(Array.isArray(data?.users) ? data.users : []);
    } catch (e) {
      setError(e.message || 'Failed to load user directory');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }

  async function runBulkScan() {
    const slice = users.slice(0, Math.min(limit, users.length));
    if (!slice.length) {
      setError('Load directory first.');
      return;
    }
    setScanning(true);
    setError('');
    const out = [];
    try {
      for (const row of slice) {
        const username = row.username || row.USERNAME || row.UserName || '';
        if (!username) continue;
        try {
          const res = await API.get(`/api/security/sod-check/${encodeURIComponent(username)}`, {
            validateStatus: () => true,
          });
          const violations = res.status === 200 ? res.data?.violations || res.data?.conflicts || [] : [];
          out.push({
            username,
            personNumber: row.personNumber || row.PERSON_NUMBER || '',
            violationCount: Array.isArray(violations) ? violations.length : 0,
            status: res.status === 200 ? (violations.length ? 'FAIL' : 'PASS') : 'ERROR',
            detail:
              res.status === 200
                ? violations.map((v) => v.ruleName || v.message || JSON.stringify(v)).join('; ')
                : res.data?.error || `HTTP ${res.status}`,
          });
        } catch (e) {
          out.push({ username, violationCount: 0, status: 'ERROR', detail: e.message });
        }
        setResults([...out]);
      }
    } finally {
      setScanning(false);
    }
  }

  function exportResults() {
    if (!results.length) return;
    downloadCsv(`sod-bulk-${Date.now()}.csv`, Object.keys(results[0]), results);
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <header>
        <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-cyan-600" />
          SoD bulk scan
        </h2>
      </header>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4">
        <button
          type="button"
          onClick={loadDirectory}
          disabled={loading || scanning}
          className="rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
        >
          {loading ? <Loader2 className="inline h-4 w-4 animate-spin" /> : null}
          Load directory
        </button>
        <label className="text-sm text-slate-600">
          Max users
          <input
            type="number"
            min={1}
            max={100}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value) || 25)}
            className="ml-2 w-16 rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
        <button
          type="button"
          onClick={runBulkScan}
          disabled={scanning || !users.length}
          className="rounded-lg border border-cyan-700 px-4 py-2 text-sm font-medium text-cyan-800 hover:bg-cyan-50 disabled:opacity-50"
        >
          {scanning ? <Loader2 className="inline h-4 w-4 animate-spin" /> : null}
          Run SoD scan
        </button>
        {results.length > 0 && (
          <button
            type="button"
            onClick={exportResults}
            className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-slate-900"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        )}
        {users.length > 0 && (
          <span className="text-xs text-slate-500">{users.length} users loaded</span>
        )}
      </div>

      {error && (
        <div className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          <AlertCircle className="h-5 w-5 shrink-0" />
          {error}
        </div>
      )}

      {results.length > 0 && (
        <div className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Username</th>
                <th className="px-3 py-2">Person #</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Violations</th>
                <th className="px-3 py-2">Detail</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.username} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-xs">{r.username}</td>
                  <td className="px-3 py-2">{r.personNumber}</td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        r.status === 'PASS'
                          ? 'text-emerald-700'
                          : r.status === 'FAIL'
                          ? 'text-rose-700 font-semibold'
                          : 'text-amber-700'
                      }
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2">{r.violationCount}</td>
                  <td className="px-3 py-2 max-w-md truncate text-xs text-slate-600" title={r.detail}>
                    {r.detail}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
